import type { ExploraPayload } from '../types/payloads/explora.types';
import type { SandboxPayload } from '../types/payloads/sandbox.types';
import type { SimulationMatrixCell, SimulationMatrixPayload } from '../schemas/mathBlocks';

const GENERIC_CELL =
  /^(efecto(\s+observado)?|notas(\s*\/?\s*estado)?|factor o acci[oó]n|operaci[oó]n( aplicada)?|valor( resultante)?|resultado|s[ií]\/?\s*no|decimal|\?+|\.{2,}|_{2,}|—|-)$/i;

function blankInput(): SimulationMatrixCell {
  return { value: '', isHiddenInput: true };
}

function visible(value: string): SimulationMatrixCell {
  return { value, isHiddenInput: false };
}

function isExploraPayload(payload: SandboxPayload): payload is ExploraPayload {
  return payload != null && typeof payload === 'object' && 'availableVariables' in payload;
}

export function isGenericMatrixCellText(value: string | undefined): boolean {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return true;
  if (GENERIC_CELL.test(trimmed)) return true;
  if (/^(paso|fila|row|ítem|item)\s*\d+$/i.test(trimmed)) return true;
  return false;
}

/** Columna donde van los factores / eventos del simulador (desde availableVariables). */
export function findSandboxFactorColumnIndex(headers: string[]): number {
  const normalized = headers.map((h) => h.toLowerCase());
  const idx = normalized.findIndex((h) =>
    /factor|acci[oó]n|evento|hecho|operaci[oó]n|causa|momento|escenario|variable|condici[oó]n|fen[oó]meno|evidencia|observaci[oó]n|elemento/.test(
      h,
    ),
  );
  return idx >= 0 ? idx : Math.min(1, Math.max(0, headers.length - 1));
}

function isLikelyStepColumn(header: string, sampleValues: string[]): boolean {
  if (/paso|#|n[°º]|orden|fase/i.test(header)) return true;
  return sampleValues.length >= 2 && sampleValues.every((v) => /^\d+$/.test(v.trim()));
}

/**
 * Inyecta labels de availableVariables en la matriz de impresión y elimina
 * strings literales de plantilla ("efecto", "notas") que la IA suele copiar.
 */
export function enrichSimulationMatrixFromExplora(
  matrix: SimulationMatrixPayload,
  payload: ExploraPayload,
): SimulationMatrixPayload {
  const vars = payload.availableVariables
    .map((v) => String(v.label || '').trim())
    .filter(Boolean);
  if (!vars.length) return sanitizeGenericMatrixCells(matrix);

  const factorCol = findSandboxFactorColumnIndex(matrix.headers);
  const stepCol = matrix.headers.findIndex((h, i) => {
    if (i === factorCol) return false;
    const samples = matrix.rows
      .map((r) => r.cells[i])
      .filter(Boolean)
      .map((c) => (c!.isHiddenInput ? c!.placeholderLabel || '' : c!.value));
    return isLikelyStepColumn(h, samples);
  });

  const rows = matrix.rows.map((row, rowIndex) => {
    const seedLabel = vars[rowIndex] ?? vars[rowIndex % vars.length];
    const cells = row.cells.map((cell) => ({ ...cell }));

    for (let col = 0; col < cells.length; col += 1) {
      const cell = cells[col];
      const text = cell.isHiddenInput ? cell.placeholderLabel || cell.value : cell.value;

      if (col === factorCol) {
        if (isGenericMatrixCellText(text)) {
          cells[col] = visible(seedLabel);
        }
        continue;
      }

      if (col === stepCol) continue;

      if (cell.isHiddenInput) {
        if (isGenericMatrixCellText(cell.placeholderLabel) || isGenericMatrixCellText(cell.value)) {
          cells[col] = blankInput();
        }
        continue;
      }

      if (isGenericMatrixCellText(cell.value)) {
        cells[col] = blankInput();
      }
    }

    return { ...row, cells };
  });

  return { ...matrix, rows };
}

/** Quita placeholders literales de plantilla en cualquier matriz. */
export function sanitizeGenericMatrixCells(matrix: SimulationMatrixPayload): SimulationMatrixPayload {
  const rows = matrix.rows.map((row) => ({
    ...row,
    cells: row.cells.map((cell) => {
      const text = cell.isHiddenInput ? cell.placeholderLabel || cell.value : cell.value;
      if (cell.isHiddenInput && isGenericMatrixCellText(cell.placeholderLabel)) {
        return blankInput();
      }
      if (!cell.isHiddenInput && isGenericMatrixCellText(cell.value)) {
        return blankInput();
      }
      if (cell.isHiddenInput && isGenericMatrixCellText(text)) {
        return blankInput();
      }
      return cell;
    }),
  }));
  return { ...matrix, rows };
}

export function enrichSimulationMatrixForSandbox(
  matrix: SimulationMatrixPayload,
  payload: SandboxPayload,
): SimulationMatrixPayload {
  if (!isExploraPayload(payload)) {
    return sanitizeGenericMatrixCells(matrix);
  }
  return enrichSimulationMatrixFromExplora(matrix, payload);
}
